/**
 * Reading and writing the Forsthofgut Akademie's tags, shared by every dashboard form that edits them.
 *
 * The Akademie keeps all its state in the `tags` map every document already has. Values are strings,
 * the same as the API (`AcademyTags.kt`) and the app (`academy.util.ts`) write them: a flag is
 * "true", a list is comma separated, a number is its digits.
 */
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import type {DataTags} from "./dataTags.util";

/** The three tracks. Fixed by the Akademie's own catalogue, not configurable per hotel. */
export const ACADEMY_LEVELS: readonly string[] = ['1', '2', '3']

/** Stamped on everything the Akademie configuration creates, its sector included. */
export const ACADEMY_BUILD_TAG = 'academy_build'

export const academyTagText = (tags: DataTags | null | undefined, key: string): string | undefined => {
    const raw = tags?.[key]
    if (typeof raw !== 'string') return undefined
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

export const academyTagList = (tags: DataTags | null | undefined, key: string): string[] =>
    (academyTagText(tags, key) ?? '')
        .split(',')
        .map(value => value.trim())
        .filter(value => value.length > 0)

export const academyTagFlag = (tags: DataTags | null | undefined, key: string): boolean =>
    (academyTagText(tags, key) ?? '').toLowerCase() === 'true'

/**
 * The tags with one value replaced.
 *
 * An empty value removes the key rather than storing "": absent is what every reader treats as
 * "not set", and a blank string left behind would be one more case each of them has to handle.
 */
export const withAcademyTag = (
    tags: DataTags | null | undefined,
    key: string,
    value: string | undefined,
): DataTags => {
    const next: DataTags = {...(tags ?? {})}
    const trimmed = value?.trim()
    if (trimmed) next[key] = trimmed
    else delete next[key]
    return next
}

/** A list written back without blanks or duplicates, in the order given. Empty removes the key. */
export const withAcademyList = (tags: DataTags | null | undefined, key: string, values: string[]): DataTags =>
    withAcademyTag(tags, key, joinList(values))

export const joinList = (values: string[]): string =>
    Array.from(new Set(values.map(v => v.trim()).filter(Boolean))).join(',')

/** The organisation's Akademie sector, if it has one. Its presence is what shows the Akademie sections. */
export const academySector = (sectors: Sector[]): Sector | undefined =>
    sectors.find(sector => !!sector.id && !!sector.tags?.[ACADEMY_BUILD_TAG])
